import { buildApplicationRequest } from "~/utils/applicationMapper"
import Logger from "@/utils/Logger"
import { ApplicationType } from "~/utils/enums/ApplicationType"

/**
 * Composable to handle application form submission
 *
 * @param formConfig The form configuration
 * @param jobId The ID of the job offer
 * @param apiConfigurationName The ATS application customer name from the job
 * @param externalId The external ID of the job offer
 * @returns Submit handler and submission state
 */
export function useApplicationFormData(formConfig: FormConfig | null, jobId: string | null, apiConfigurationName: string | null, externalId: string | null) {
  const { $api } = useNuxtApp()
  const { tenantId } = useHeaderStore()
  const { data: jobShop } = useNuxtData("jobShopData")
  const { t } = useI18n()

  const isSubmitting = ref(false)
  const submitError = ref<string | null>(null)
  const submitSuccess = ref(false)
  const externalFieldErrors = ref<Record<string, string>>({})
  const genericBackendError = ref<string | null>(null)

  /**
   * Handles form submission
   * @param submitData The submit data emitted from CRFormRenderer
   */
  const handleSubmit = async (submitData: { valid: boolean; values: Record<string, any>; errors: Record<string, any> }) => {
    if (!submitData.valid) {
      return
    }

    if (!jobId) {
      submitError.value = "Job ID is required"
      return
    }

    isSubmitting.value = true
    submitError.value = null
    submitSuccess.value = false

    try {
      trackApplicationSubmit("form_submit")

      if (!formConfig) {
        submitError.value = "Form configuration is required"
        return
      }

      // Determine which ID to use based on application type
      const effectiveJobId = formConfig.form.applicationType === ApplicationType.FAST_APPLICATION ? externalId : jobId

      if (!effectiveJobId) {
        submitError.value = "Job ID is required"
        return
      }

      // Get query headers and convert to customFields
      const queryHeaders = useQueryHeaders(useRequestHeaders())
      const customFields: CustomField[] = queryHeaders.value.map((header) => ({
        name: header.key,
        value: header.value,
      }))

      // Build the application request from form data
      const applicationRequest = await buildApplicationRequest(
        submitData.values,
        formConfig,
        {
          jobID: effectiveJobId,
          apiConfigurationName: apiConfigurationName || "email",
          applicationType: formConfig.form.applicationType,
        },
        customFields
      )

      // Send the application
      await $api.application.sendApplication(
        tenantId,
        jobShop.value?.locale,
        applicationRequest
      )

      submitSuccess.value = true
      trackApplicationSubmit("success")
    }
    catch (error: any) {
      externalFieldErrors.value = {}
      genericBackendError.value = null

      const data = (error && (error.data || error.response?.data || error.response?._data)) || error?.data
      const errors = Array.isArray(data?.errors) ? data.errors : []

      if (errors.length > 0 && formConfig && formConfig.form && Array.isArray(formConfig.form.fields)) {
        const fieldErrors: Record<string, string> = {}

        const fieldNames = new Set(formConfig.form.fields.map((field) => field.name))
        const pathToNameMap: Record<string, string> = {}

        for (const field of formConfig.form.fields) {
          if (field && typeof field.path === "string" && field.path) {
            pathToNameMap[field.path] = field.name
          }
        }

        for (const item of errors) {
          if (!item || !item.field || !item.message) {
            continue
          }

          let targetFieldName: string | null = null

          if (fieldNames.has(item.field)) {
            targetFieldName = item.field
          }
          else if (pathToNameMap[item.field]) {
            targetFieldName = pathToNameMap[item.field]
          }

          if (!targetFieldName) {
            continue
          }

          fieldErrors[targetFieldName] = item.message
        }

        externalFieldErrors.value = fieldErrors
      } else {
        genericBackendError.value = t("application.generic-error")
      }

      submitError.value = genericBackendError.value
      Logger().error("Error submitting application:", error)
      trackApplicationSubmit("error")
    }
    finally {
      isSubmitting.value = false
    }
  }

  return {
    handleSubmit,
    isSubmitting,
    submitError,
    submitSuccess,
    externalFieldErrors,
    genericBackendError,
  }
}
